import { yupResolver } from '@hookform/resolvers/yup'
import { t } from '@if/core/i18n'
import {
  ControlledDateOnlyPicker,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  stopPropagationSubmitHandler,
} from '@if/mui'
import { MenuItem, Stack, Typography } from '@mui/material'
import { Metro2ReportingSchema, Metro2ReportingSchemaType } from '@src/components/LoanForm/loan-form-schema'
import { EMetro2ReportingReason, getEMetro2ReportingReasonList } from '@src/types/constants'
import { DateOnly } from 'node_modules/@if/core/dist/date/dateOnly'
import { JSX, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import FormDialog from '../../Common/Dialog/FormDialog'

interface Props {
  onClose: () => void
  onSubmit: (data: Metro2ReportingSchemaType) => void
  open: boolean
  value: Metro2ReportingSchemaType | null
}

const EditMetro2Reporting = ({ onClose, onSubmit, open, value }: Props): JSX.Element => {
  const { handleSubmit, reset, control } = useForm<Metro2ReportingSchemaType>({
    resolver: yupResolver(Metro2ReportingSchema),
    defaultValues: value ?? undefined,
  })

  // When updating the value, reset the form
  useEffect(() => {
    if (value) {
      reset(value ?? Metro2ReportingSchema.getDefault())
    }
  }, [value, reset])

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmitHandler = (data: Metro2ReportingSchemaType) => {
    onSubmit(data)
    handleClose()
  }

  const metro2Reasons: EMetro2ReportingReason[] = getEMetro2ReportingReasonList()

  return (
    <FormDialog
      onClose={handleClose}
      open={open}
      maxWidth="sm"
      confirmLabel={t('common.save')}
      title={<Typography>{t('loan.payment.creditReportDialog.editDate')}</Typography>}
      onConfirm={stopPropagationSubmitHandler(handleSubmit(onSubmitHandler))}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <ControlledDateOnlyPicker
            label={t('loan.payment.creditReportDialog.date')}
            name="metro2OverrideDate"
            control={control}
            shouldDisableDate={(date) => {
              const today = new DateOnly()
              const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
              return date < firstDayOfCurrentMonth // Ensure the override date is not selectable if anterior to the current month
            }}
          />
          <ControlledSelect
            control={control}
            name="metro2ReportingReason"
            label={t('loan.payment.creditReportDialog.reason')}
          >
            {metro2Reasons.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {t(`enum.EMetro2ReportingReason.${reason}`)}
              </MenuItem>
            ))}
          </ControlledSelect>
        </Stack>
        <ControlledTextField
          label={t('loan.payment.creditReportDialog.desc')}
          multiline
          rows={5}
          fullWidth
          control={control}
          name="description"
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <ControlledTextField
            label={t('loan.payment.creditReportDialog.createdBy')}
            fullWidth
            control={control}
            name="createdByUserFullname"
            disabled
          />
          <ControlledDatePicker
            label={t('loan.payment.creditReportDialog.createdOn')}
            control={control}
            name="createdOn"
            disabled
          />
        </Stack>
      </Stack>
    </FormDialog>
  )
}

export default EditMetro2Reporting
